<template>
  <div class="card table-list-card">
    <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
      <div class="search-set">
        <div class="search-input">
          <span class="btn-searchset">
            <i class="ti ti-search fs-14 feather-search"></i>
          </span>
          <input
            v-model="searchInput"
            type="search"
            class="form-control"
            :placeholder="searchPlaceholder"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div
        v-if="filters.length"
        class="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3"
      >
        <div
          v-for="(filter, index) in filters"
          :key="filter.key"
          :class="[
            filter.type === 'select2' ? 'theme-dt-select2' : 'dropdown',
            { 'me-2': index < filters.length - 1 },
          ]"
        >
          <template v-if="filter.type === 'select2'">
            <FormDropdown
              :model-value="selectedValues[filter.key]"
              :options="getSelect2Options(filter)"
              :placeholder="filter.label"
              clearable
              @update:model-value="(value) => applyFilter(filter, value)"
            />
          </template>
          <template v-else>
            <a
              href="javascript:void(0);"
              class="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
              data-bs-toggle="dropdown"
            >
              {{ selectedLabels[filter.key] || filter.label }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click.prevent="applyFilter(filter, '')"
                >
                  {{ filter.label }}
                </a>
              </li>
              <li v-for="option in getFilterOptions(filter)" :key="`${filter.key}-${option}`">
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click.prevent="applyFilter(filter, option)"
                >
                  {{ option }}
                </a>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>

    <div class="card-body p-0">
      <div class="table-responsive">
        <div class="table-wrapper px-3 pb-3">
          <div v-if="loading" class="theme-dt-loading text-center py-4 text-muted">
            Loading...
          </div>

          <table
            :id="tableId"
            class="table table-hover w-100"
            :class="{ 'theme-dt-dimmed': loading }"
            @click="onTableClick"
          >
            <slot :rows="rows" :loading="loading"></slot>
          </table>

          <div
            v-if="!loading && !rows.length"
            class="d-flex flex-column align-items-center justify-content-center p-5"
          >
            <h5 class="text-muted">{{ emptyTitle }}</h5>
            <p class="text-secondary small mb-0">{{ emptyHint }}</p>
          </div>

          <div
            v-if="recordsFiltered > 0"
            class="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2 theme-dt-footer"
          >
            <div class="d-flex align-items-center gap-2">
              <select
                v-model.number="pageLength"
                class="form-select form-select-sm theme-dt-length"
                @change="onLengthChange"
              >
                <option v-for="size in pageLengthOptions" :key="size" :value="size">
                  {{ size }}
                </option>
              </select>
              <span class="theme-dt-info">
                {{ infoText }}
              </span>
            </div>

            <ul class="pagination mb-0 theme-dt-paginate">
              <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                <a
                  class="page-link"
                  href="javascript:void(0);"
                  @click.prevent="goToPage(currentPage - 1)"
                >
                  <i class="fas fa-arrow-left"></i>
                </a>
              </li>
              <li
                v-for="page in visiblePages"
                :key="`p-${page}`"
                class="page-item"
                :class="{ active: page === currentPage, disabled: page === '...' }"
              >
                <a
                  class="page-link"
                  href="javascript:void(0);"
                  @click.prevent="page !== '...' && goToPage(page)"
                >
                  {{ page }}
                </a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                <a
                  class="page-link"
                  href="javascript:void(0);"
                  @click.prevent="goToPage(currentPage + 1)"
                >
                  <i class="fas fa-arrow-right"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import FormDropdown from '@/components/common/FormDropdown.vue';

const props = defineProps({
  tableId: {
    type: String,
    required: true,
  },
  /**
   * async (params) => { data, recordsTotal, recordsFiltered }
   * params: { draw, start, length, search, orderColumn, orderDir, filters }
   */
  fetchData: {
    type: Function,
    required: true,
  },
  /** Filter config: { key, label, param?, options?, type?: 'select2' } */
  filters: {
    type: Array,
    default: () => [],
  },
  pageLength: {
    type: Number,
    default: 10,
  },
  pageLengthOptions: {
    type: Array,
    default: () => [10, 25, 50, 100],
  },
  searchPlaceholder: {
    type: String,
    default: 'Search',
  },
  emptyTitle: {
    type: String,
    default: 'No Data Found',
  },
  emptyHint: {
    type: String,
    default: 'Try adjusting filters or add new data.',
  },
  defaultOrderColumn: {
    type: Number,
    default: 0,
  },
  defaultOrderDir: {
    type: String,
    default: 'asc',
  },
});

const emit = defineEmits(['loaded']);

const rows = ref([]);
const loading = ref(false);
const searchInput = ref('');
const search = ref('');
const start = ref(0);
const pageLength = ref(props.pageLength);
const recordsTotal = ref(0);
const recordsFiltered = ref(0);
const draw = ref(0);
const orderColumn = ref(props.defaultOrderColumn);
const orderDir = ref(props.defaultOrderDir);
const selectedLabels = reactive({});
const selectedValues = reactive({});

let searchTimer = null;
let requestSeq = 0;

const currentPage = computed(() => Math.floor(start.value / pageLength.value) + 1);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(recordsFiltered.value / pageLength.value) || 1)
);

const infoText = computed(() => {
  if (!recordsFiltered.value) return '0 - 0 of 0 items';
  const from = start.value + 1;
  const to = Math.min(start.value + pageLength.value, recordsFiltered.value);
  return `${from} - ${to} of ${recordsFiltered.value} items`;
});

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  return [...pages]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b)
    .reduce((acc, page, idx, arr) => {
      if (idx > 0 && page - arr[idx - 1] > 1) acc.push('...');
      acc.push(page);
      return acc;
    }, []);
});

function extractSelectValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value.id !== undefined && value.id !== null && value.id !== '') {
      return String(value.id);
    }
    if (value.text !== undefined && value.text !== null && value.text !== '') {
      return String(value.text);
    }
    return '';
  }
  return String(value);
}

function getFilterOptions(filter) {
  if (Array.isArray(filter.options) && filter.options.length) {
    return [...new Set(filter.options.filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }
  return [];
}

function getSelect2Options(filter) {
  return getFilterOptions(filter).map((option) => ({
    id: option,
    text: option,
  }));
}

function activeFilters() {
  const result = {};
  for (const filter of props.filters) {
    const value = selectedValues[filter.key];
    if (value != null && String(value).trim() !== '') {
      result[filter.param || filter.key] = String(value).trim();
    }
  }
  return result;
}

async function load() {
  const seq = ++requestSeq;
  loading.value = true;
  try {
    const result = await props.fetchData({
      draw: ++draw.value,
      start: start.value,
      length: pageLength.value,
      search: search.value,
      orderColumn: orderColumn.value,
      orderDir: orderDir.value,
      filters: activeFilters(),
    });

    if (seq !== requestSeq) return;

    rows.value = Array.isArray(result?.data) ? result.data : [];
    recordsTotal.value = Number(result?.recordsTotal) || 0;
    recordsFiltered.value = Number(result?.recordsFiltered) || 0;
    emit('loaded', {
      rows: rows.value,
      recordsTotal: recordsTotal.value,
      recordsFiltered: recordsFiltered.value,
    });
  } catch (error) {
    if (seq !== requestSeq) return;
    console.error('ThemeDataTable fetch failed:', error);
    rows.value = [];
    recordsTotal.value = 0;
    recordsFiltered.value = 0;
  } finally {
    if (seq === requestSeq) {
      loading.value = false;
    }
  }
}

function onSearchInput() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    search.value = (searchInput.value || '').trim();
    start.value = 0;
    load();
  }, 350);
}

function applyFilter(filter, value) {
  const normalized = extractSelectValue(value);
  selectedLabels[filter.key] = normalized;
  selectedValues[filter.key] = normalized;
  start.value = 0;
  load();
}

function onLengthChange() {
  start.value = 0;
  load();
}

function goToPage(page) {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  start.value = (page - 1) * pageLength.value;
  load();
}

function setOrder(columnIndex, dir = null) {
  if (orderColumn.value === columnIndex) {
    orderDir.value = dir || (orderDir.value === 'asc' ? 'desc' : 'asc');
  } else {
    orderColumn.value = columnIndex;
    orderDir.value = dir || 'asc';
  }
  start.value = 0;
  load();
}

function onTableClick(event) {
  const th = event.target.closest('thead th');
  if (!th || th.classList.contains('no-sort')) return;
  const headers = [...th.closest('thead').querySelectorAll('th')];
  const index = headers.indexOf(th);
  if (index >= 0) setOrder(index);
}

function reload() {
  return load();
}

onMounted(() => {
  load();
});

defineExpose({
  reload,
  setOrder,
  load,
});
</script>

<style scoped>
.search-input {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input .btn-searchset {
  position: absolute;
  left: 12px;
  z-index: 1;
  pointer-events: none;
  color: #6c757d;
}

.search-input .form-control {
  padding-left: 36px;
  height: 38px;
  min-width: 220px;
  border: 1px solid #E6EAED;
  border-radius: 5px;
}

.theme-dt-loading {
  font-size: 14px;
}

.theme-dt-dimmed {
  opacity: 0.55;
  pointer-events: none;
}

.theme-dt-footer {
  padding-bottom: 0.5rem;
}

.theme-dt-length {
  width: auto;
  min-width: 70px;
  height: 32px;
  font-size: 12px;
}

.theme-dt-info {
  white-space: nowrap;
  padding-right: 15px;
  font-size: 12px;
  color: #495057;
  font-weight: 600;
}

.theme-dt-paginate {
  display: flex;
  gap: 5px;
  margin-left: auto;
}

.table-wrapper {
  padding-top: 0.5rem;
}

:deep(thead th:not(.no-sort)) {
  cursor: pointer;
  user-select: none;
}

/* Match table search input height (38px) */
.theme-dt-select2 {
  width: 200px;
}

.theme-dt-select2 :deep(.form-basic-dropdown > .btn) {
  min-height: 38px;
}
</style>
